import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är voice over?",
    a: "Voice over är inspelad berättarröst som läggs ovanpå bild i film, animation eller reklam. Rösten hörs men syns inte i bild, och skiljer sig därför från dialog och synk-repliker som spelas in samtidigt som bilden.",
  },
  {
    q: "Vad kostar en voice over i Sverige?",
    a: "En professionell svensk speaker för företagsfilm eller förklaringsfilm kostar typiskt 4 000–9 000 kr för manus upp till cirka 400 ord, inklusive studio och ljudmix. Reklamfilm med bred mediaanvändning kostar mer eftersom användningsrätten prissätts separat, ofta 10 000–25 000 kr.",
  },
  {
    q: "Hur långt manus behövs per minut film?",
    a: "Räkna med cirka 130–150 svenska ord per minut i lugnt berättartempo. En 60-sekundersfilm rymmer alltså ungefär 140 ord — mindre om bilden ska få tala emellan.",
  },
  {
    q: "Ska vi använda AI-röst eller en riktig speaker?",
    a: "AI-röst fungerar för internt material, snabba uppdateringar och versioner på många språk där tonen inte är avgörande. För varumärkesbärande reklam och film där trovärdighet är poängen rekommenderar vi fortfarande en mänsklig speaker — nyansen i betoning gör skillnad för hur budskapet uppfattas.",
  },
  {
    q: "Hur lång tid tar det att få en voice over?",
    a: "Från godkänt manus till färdig ljudfil tar det normalt 2–5 arbetsdagar, inklusive castingförslag, inspelning och mix. Brådskande uppdrag kan levereras inom 24 timmar.",
  },
  {
    q: "Ingår voice over i era produktioner?",
    a: "Ja. Speaker, ljudläggning, musik och mix ingår i våra film- och animationsproduktioner. Vi tar även fristående uppdrag där ni har bilden klar men saknar röst.",
  },
];

export const Route = createFileRoute("/voice-over")({
  head: () => ({
    meta: [
      { title: "Voice over — pris, röstcasting och manuslängd | Fenny" },
      {
        name: "description",
        content:
          "Voice over förklarat: vad en speaker kostar i Sverige 2026, hur långt manus per minut, AI-röst vs mänsklig speaker och hur inspelningen går till.",
      },
      { property: "og:title", content: "Voice over — pris, röstcasting och manuslängd" },
      {
        property: "og:description",
        content: "Pris, ordantal per minut och valet mellan AI-röst och mänsklig speaker — praktisk guide inför er nästa film.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://fenny.se/voice-over" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/voice-over" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/voice-over", name: "Voice over — pris, röstcasting och manuslängd | Fenny", description: "Voice over förklarat: vad en speaker kostar i Sverige 2026, hur långt manus per minut, AI-röst vs mänsklig speaker och hur inspelningen går till."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Voice over och speakerinspelning",
            path: "/voice-over",
            serviceType: "Ljudproduktion",
            description:
              "Voice over för film, animation och reklam — röstcasting, studioinspelning, ljudläggning och mix.",
            priceRange: "4 000–25 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Voice over", path: "/voice-over" },
          ]),
        ),
      },
    ],
  }),
  component: VoiceOverPage,
});

function VoiceOverPage() {
  return (
    <LandingPage
      eyebrow="Voice over"
      title={<>Voice over — rösten som avgör om budskapet landar.</>}
      lede="Röstcasting, studioinspelning, ljudläggning och mix för film, animation och reklam. Vi tar fram två till tre röstförslag mot ert manus så att ni hör skillnaden innan ni väljer."
      tldr={
        <p>
          Voice over är berättarröst som läggs ovanpå bild. Pris i Sverige:{" "}
          <strong>4 000–9 000 kr</strong> för företagsfilm och förklaringsfilm
          upp till cirka 400 ord, <strong>10 000–25 000 kr</strong> för reklam
          med bred mediaanvändning. Räkna med <strong>130–150 ord per minut</strong>{" "}
          film och 2–5 arbetsdagars leveranstid.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se priser", to: "/priser" }}
      trackingId="voice-over"
      bullets={[
        { title: "Röstförslag först", body: "Två till tre speakers läser in ert eget manus innan ni bestämmer er." },
        { title: "Rättigheter klart", body: "Användningsområde och tid specificeras i offerten — inga efterfakturor." },
        { title: "Mix som håller", body: "Nivåsatt mot musik och effekter så att rösten hörs även i mobilhögtalare." },
      ]}
      pricing={{
        heading: "Vad kostar en voice over?",
        intro: "Riktpriser exklusive moms för svensk speaker inklusive studio och mix.",
        rows: [
          { format: "Företagsfilm / förklaringsfilm", scope: "Upp till ca 400 ord, webb och interna kanaler", price: "4 000–9 000 kr", lead: "2–5 dagar" },
          { format: "Reklamfilm", scope: "Bred mediaanvändning, rättigheter 12 mån", price: "10 000–25 000 kr", lead: "3–5 dagar" },
          { format: "Extra språkversion", scope: "Samma manus, annan speaker och mix", price: "3 000–8 000 kr", lead: "+2 dagar" },
          { format: "AI-röst", scope: "Interna filmer och snabba uppdateringar", price: "från 1 500 kr", lead: "1 dag" },
        ],
        footnote: "Ingår utan tillägg i löpande produktion via Pulse-abonnemang.",
      }}
      process={{
        heading: "Så går en speakerinspelning till",
        steps: [
          { label: "Manus och tempo", body: "Vi kortar manuset till rätt ordantal för filmens längd och sätter tonalitet.", duration: "dag 1" },
          { label: "Röstcasting", body: "Två till tre röstförslag inlästa mot ert faktiska manus.", duration: "dag 1–2" },
          { label: "Inspelning", body: "Studioinspelning med regi på betoning, tempo och pauser.", duration: "dag 2–3" },
          { label: "Mix och leverans", body: "Rensning, nivåsättning mot musik och effekter, leverans i WAV och färdig film.", duration: "dag 3–5" },
        ],
      }}
      sections={[
        {
          id: "manuslangd",
          heading: "Hur långt ska manuset vara?",
          body: (
            <>
              <p>
                Cirka 130–150 svenska ord per minut är ett bekvämt berättartempo.
                En 30-sekundersfilm rymmer alltså runt 70 ord, en
                60-sekundersfilm runt 140 ord och en tre minuter lång
                förklaringsfilm cirka 400 ord.
              </p>
              <p>
                Vanligaste misstaget är att skriva manuset som en text som ska
                läsas, inte höras. Läs alltid högt med klocka innan inspelning —
                det som tar 45 sekunder i huvudet tar 70 i studion.
              </p>
            </>
          ),
        },
        {
          id: "ai-vs-manniska",
          heading: "AI-röst eller mänsklig speaker?",
          body: (
            <>
              <p>
                AI-röster har blivit tillräckligt bra för internt material,
                instruktionsfilm och snabba språkversioner där tonen inte är
                avgörande. Fördelen är pris och att texten kan uppdateras utan ny
                inspelning.
              </p>
              <p>
                För varumärkesbärande{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a>{" "}
                håller vi fortfarande på en mänsklig speaker. Betoning, andning
                och timing är det som gör att en mening låter menad snarare än
                uppläst.
              </p>
            </>
          ),
        },
        {
          id: "anvandning",
          heading: "Var voice over gör mest nytta",
          body: (
            <p>
              I{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-forklaringsfilm">animerad förklaringsfilm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/motion-graphics">motion graphics</a>,{" "}
              utbildnings- och instruktionsfilm samt{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktfilm">produktfilm</a>{" "}
              där ingen talare finns i bild. Komplettera alltid med undertexter — merparten av visningarna i sociala kanaler sker utan ljud.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={4}
      riskReducers
      closing={{
        title: "Behöver ni en voice over?",
        body: "Skicka manuset så återkommer vi med röstförslag och fast pris.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Se case", to: "/case" },
      }}
    />
  );
}
